import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useDrag, useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import {
    Braces,
    Check,
    ChevronRight,
    Download,
    Eye,
    File as FileIcon,
    FileCode2,
    FileJson2,
    FileSpreadsheet,
    FileText,
    Folder as FolderIcon,
    FolderOpen,
    FolderPlus,
    FolderTree,
    Image as ImageIcon,
    Loader2,
    MoreHorizontal,
    Pencil,
    Presentation,
    RefreshCw,
    Search,
    SearchX,
    Trash2,
    Upload,
    UploadCloud,
    X,
} from "lucide-react";

import { EASE_OUT, SPRING_LAYOUT, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

import { useDidMountEffect } from "../utils";
import { hintPlacement } from "./treeHint";

export interface TreeNode {
    id?: string;
    fileId?: string;
    state?: string;
    name: string;
    type: "file" | "folder";
    files?: TreeNode[];
    parent?: TreeNode;
    processing?: boolean;
    processingState?: string;
    selected?: boolean;
    /**
     * Die Zeile ist mitausgewählt, weil ein Ordner über ihr ausgewählt wurde — nicht, weil jemand
     * sie selbst angehakt hat. Das Häkchen steht, lässt sich aber nicht einzeln zurücknehmen: Die
     * Auswahl benennt den Ordner, sein Inhalt wird serverseitig aufgelöst.
     */
    selectionLocked?: boolean;
    isOpen?: boolean;
    isUploading?: boolean;
    path?: string;
    onSelect?: (checked: boolean) => void;
    onClick?: () => void;
    onEdit?: () => void;
    onDelete?: (deleteCallback: () => void) => void;
    onCreateFolder?: (name: string, createCallback: () => void) => void;
    onCreateFile?: (name: string, createCallback: () => void) => void;
    onRename?: (name: string) => Promise<void>;
    onDownload?: () => void;
    /** Öffnet die Datei im Dokumentbetrachter; ohne Rückruf gibt es kein „Ansehen" im Menü. */
    onView?: () => void;
    onFileDrop?: (files: File[], folderPath: string) => Promise<void>;
    onFileMove?: (fileItem: any, folderPath: string) => Promise<void>;
    onToggleOpen?: (isOpen: boolean) => void;
    showCheckbox?: boolean;
    /**
     * Ein freies Abzeichen an der Zeile.
     *
     * `processingState` reicht dafür nicht: Es schlägt einen festen Übersetzungsschlüssel nach und
     * verschwindet stillschweigend, wenn keiner passt. Der Datenraum braucht freien Text — den
     * Schritt eines laufenden Analyselaufs —, und er braucht das Abzeichen als Unterscheidung:
     * Wo eines steht, hängt eine Dokumentenanalyse an der Datei; wo keines steht, ist es eine
     * gewöhnliche Datei.
     */
    badge?: { text: string; tone?: "info" | "ok" | "warn" | "error"; busy?: boolean };
    /**
     * Ein zweites Abzeichen, hinter dem ersten.
     *
     * Das erste sagt, *was* die Datei ist — die erkannte Dokumentenart oder der laufende Schritt.
     * Das zweite sagt, dass an ihr noch etwas zu tun ist: offene Entscheidungen aus dem Lauf. Beides
     * in ein Abzeichen zu falten hieße, dass die Aufforderung die Auskunft verdrängt oder umgekehrt
     * — und die offenen Entscheidungen gehören an die Zeile, weil man sie sonst nur findet, indem
     * man jede Datei einzeln anklickt.
     */
    secondaryBadge?: { text: string; tone?: "info" | "ok" | "warn" | "error"; busy?: boolean };
    /**
     * Was am Mauszeiger steht.
     *
     * Der Baum steht in einer schmalen Spalte, und ein Dateiname überlebt das selten ganz — von dem,
     * was in der Datei steht, ganz zu schweigen. Der Kurzhinweis ist die Stelle, an der beides
     * Platz hat, ohne dass die Zeile breiter wird.
     */
    hint?: { title: string; text?: string | null };
    /**
     * Blass gezeichnet: Die Datei ist angekündigt, aber noch nicht da.
     *
     * Der Platzhalter ist damit dieselbe Zeile wie später die Datei — an derselben Stelle, mit
     * demselben Symbol, nur ausgegraut. Ein eigener Balken über der Liste würde sagen, dass
     * *irgendetwas* läuft; hier steht, was mit *dieser* Datei geschieht, und die Zeile springt
     * nicht, wenn die Ablage sie kennt.
     */
    dimmed?: boolean;
    /**
     * Ein kleines Zeichen auf dem Dateisymbol.
     *
     * Das Symbol selbst bleibt das der Dateiart — ob etwas ein PDF ist, will man weiter auf einen
     * Blick sehen. Darüber sitzt das Zeichen, das sagt, dass an dieser Datei mehr hängt als an
     * einer gewöhnlichen: im Datenraum eine Dokumentenanalyse.
     */
    iconMark?: { tone?: "info" | "ok" | "warn" | "error" };
}

interface TreeProps {
    children?: React.ReactNode;
    data?: TreeNode[];
    /** Skeleton rows while the first storage listing loads. */
    isLoading?: boolean;
    /** Path of the file currently shown in the details panel — rendered as the selected row. */
    selectedPath?: string;
    onNodeClick?: (node: {
        state: TreeNode[];
        name: string;
        parent?: TreeNode;
        type: string;
        level?: number;
        path?: string;
    }) => void;
    onUpdate?: (state: TreeNode[]) => void;
    onFileDrop?: (files: File[], folderPath: string) => Promise<void>;
    onFileMove?: (fileItem: any, folderPath: string) => Promise<void>;
    onCreateRootFolder?: (name: string) => Promise<void>;
    /** Reloads the file listing (bypassing the debounce) when the refresh icon is pressed. */
    onRefresh?: () => Promise<void> | void;
    /** Überschrift des Kopfbereichs. Ohne Angabe „Dokumente", wie in der Dateiverwaltung. */
    title?: string;
    /**
     * Gedrängt: kleinere Schrift und niedrigere Zeilen.
     *
     * Für den Baum in einer schmalen Spalte — im Dokumentenkamin des Dashboards. Die
     * Dateiverwaltung hat eine ganze Seite und bleibt, wie sie ist; eine Größe für beide wäre dort
     * zu klein und hier zu groß.
     */
    dense?: boolean;
    /**
     * „Löschen" auch an gefüllten Ordnern anbieten.
     *
     * Der Server löscht einen Ordner ohnehin samt Inhalt; ob der Baum das anbietet, ist eine Frage
     * der Ansicht. Die Dateiverwaltung bestätigt jedes Löschen per Dialog mit Nachtippen — dort
     * darf ein ganzer Ordner weg. Wo ohne Rückfrage gelöscht wird, bleibt es beim leeren Ordner.
     */
    deleteNonEmptyFolders?: boolean;
}

const INDENT = 18;

/** Wie breit der Kurzhinweis höchstens wird, und wie lange er auf sich warten lässt. */
const HINT_WIDTH = 340;
const HINT_DELAY_MS = 250;

const ROW_ENTER = { duration: 0.22, ease: EASE_OUT } as const;
const ROW_EXIT = { duration: 0.14, ease: EASE_OUT } as const;
const BRANCH_DRAW = { duration: 0.3, ease: EASE_OUT } as const;

const nodeKey = (node: TreeNode) => `${node.type}:${node.path ?? node.id ?? node.name}`;

/** Extension → colored lucide icon, mirroring the beui file-tree demo palette. */
export const fileIconForName = (name: string): JSX.Element => {
    const ext = (name.split(".").pop() || "").toLowerCase();
    switch (ext) {
        case "pdf":
            return <FileText className="size-4 text-red-500" />;
        case "doc":
        case "docx":
            return <FileText className="size-4 text-blue-500" />;
        case "xls":
        case "xlsx":
        case "csv":
            return <FileSpreadsheet className="size-4 text-emerald-500" />;
        case "ppt":
        case "pptx":
            return <Presentation className="size-4 text-orange-500" />;
        case "png":
        case "jpg":
        case "jpeg":
        case "gif":
        case "svg":
        case "webp":
            return <ImageIcon className="size-4 text-violet-500" />;
        case "json":
            return <FileJson2 className="size-4 text-amber-500" />;
        case "js":
        case "jsx":
        case "ts":
        case "tsx":
            return <Braces className="size-4 text-sky-500" />;
        case "html":
        case "css":
            return <FileCode2 className="size-4 text-sky-500" />;
        case "md":
        case "txt":
            return <FileText className="size-4 text-muted-foreground" />;
        default:
            return <FileIcon className="size-4 text-muted-foreground" />;
    }
};

/** Open/closed folder icon that cross-fades with a little pop, à la beui. */
const AnimatedFolderIcon = ({ open, reduce }: { open: boolean; reduce: boolean }) => {
    if (reduce) {
        return open ? (
            <FolderOpen className="size-4" />
        ) : (
            <FolderIcon className="size-4" />
        );
    }
    return (
        <AnimatePresence initial={false} mode="popLayout">
            <motion.span
                key={open ? "open" : "closed"}
                initial={{ opacity: 0, scale: 0.75, rotate: open ? -8 : 8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.75, rotate: open ? 8 : -8 }}
                transition={SPRING_SWAP}
                className="absolute inset-0 grid place-items-center"
            >
                {open ? <FolderOpen className="size-4" /> : <FolderIcon className="size-4" />}
            </motion.span>
        </AnimatePresence>
    );
};

/** Indeterminate shimmer under a folder row while its upload runs. */
const UploadShimmer = () => (
    <span className="pointer-events-none absolute inset-x-2 bottom-0 h-0.5 overflow-hidden rounded-full bg-primary/15">
        <motion.span
            className="absolute inset-y-0 w-1/3 rounded-full bg-primary/70"
            animate={{ x: ["-100%", "300%"] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        />
    </span>
);

/** Das freie Abzeichen des Datenraums — Ton und Drehkreis, sonst nichts. */
const NodeBadge = ({ badge }: { badge: NonNullable<TreeNode["badge"]> }) => (
    <span
        className={cn(
            // `min-w-0` und `truncate`: In einer schmalen Spalte darf ein langer Dokumenttyp den
            // Dateinamen nicht verdrängen — er ist die Auskunft, der Name ist die Sache selbst.
            "inline-flex min-w-0 shrink items-center gap-1 truncate rounded-full px-2 py-0.5 text-[11px] leading-4",
            badge.tone === "error"
                ? "bg-destructive/10 text-destructive"
                : badge.tone === "ok"
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : badge.tone === "warn"
                        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        : "bg-muted text-muted-foreground",
        )}
    >
        {badge.busy && <Loader2 aria-hidden className="size-3 animate-spin" />}
        {badge.text}
    </span>
);

const ProcessingBadge = ({
    processing,
    processingState,
}: {
    processing?: boolean;
    processingState?: string;
}) => {
    const { t } = useTranslation();
    if (!processingState) return null;
    const label = t("fileStates." + processingState, { defaultValue: "" });
    if (!label) return null;
    const isError = processingState === "failed" || processingState === "error";
    return (
        <span
            className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] leading-4",
                isError
                    ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-muted-foreground",
            )}
        >
            {processing && <Loader2 aria-hidden className="size-3 animate-spin" />}
            {label}
        </span>
    );
};

/** Inline row for renaming a node or naming a freshly created folder. */
const InlineNameRow = ({
    depth,
    type,
    defaultValue = "",
    onSubmit,
    onCancel,
}: {
    depth: number;
    type: "file" | "folder";
    defaultValue?: string;
    onSubmit: (name: string) => void | Promise<void>;
    onCancel: () => void;
}) => {
    const [value, setValue] = useState(defaultValue);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittedRef = useRef(false);

    const submit = async () => {
        const name = value.trim();
        if (!name || submittedRef.current) return;
        submittedRef.current = true;
        setIsSubmitting(true);
        try {
            await onSubmit(name);
        } catch (error) {
            console.error("Inline name submit failed:", error);
            submittedRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="relative flex h-9 w-full min-w-0 items-center gap-2 rounded-lg pr-1.5"
            style={{ paddingLeft: 8 + depth * INDENT }}
        >
            <span className="grid size-4 shrink-0 place-items-center" aria-hidden>
                {type === "folder" ? (
                    <FolderIcon className="size-4 text-muted-foreground" />
                ) : (
                    fileIconForName(value)
                )}
            </span>
            <Input
                autoFocus
                value={value}
                disabled={isSubmitting}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        submit();
                    } else if (event.key === "Escape") {
                        event.preventDefault();
                        onCancel();
                    }
                }}
                onBlur={() => {
                    if (!submittedRef.current) onCancel();
                }}
                className="h-7 min-w-0 flex-1 text-sm"
                placeholder={type === "folder" ? "Ordnername" : "Dateiname"}
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-primary"
                disabled={isSubmitting}
                // preventDefault so the input's blur-cancel doesn't fire before the click
                onMouseDown={(event) => event.preventDefault()}
                onClick={submit}
                title="Speichern"
            >
                {isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                    <Check className="size-4" aria-hidden />
                )}
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground"
                disabled={isSubmitting}
                onMouseDown={(event) => event.preventDefault()}
                onClick={onCancel}
                title="Abbrechen"
            >
                <X className="size-4" aria-hidden />
            </Button>
        </div>
    );
};

/** Marks the matching substring while a search query is active. */
const HighlightedName = ({ name, query }: { name: string; query: string }) => {
    if (!query) return <>{name}</>;
    const index = name.toLowerCase().indexOf(query);
    if (index === -1) return <>{name}</>;
    return (
        <>
            {name.slice(0, index)}
            <mark className="rounded-sm bg-primary/20 text-inherit">
                {name.slice(index, index + query.length)}
            </mark>
            {name.slice(index + query.length)}
        </>
    );
};

const SKELETON_ROWS: Array<[depth: number, width: number]> = [
    [0, 112], [0, 88], [1, 132], [1, 96], [2, 120], [2, 76], [1, 104], [0, 140], [0, 84],
];

const TreeSkeleton = () => (
    <div className="flex w-full flex-col" aria-hidden>
        {SKELETON_ROWS.map(([depth, width], index) => (
            <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, ease: EASE_OUT, delay: index * 0.045 }}
                className="flex h-9 items-center gap-2 pr-2"
                style={{ paddingLeft: 8 + depth * INDENT }}
            >
                <Skeleton className="size-4 shrink-0 rounded" />
                <Skeleton className="size-4 shrink-0 rounded" />
                <Skeleton className="h-3 rounded-full" style={{ width }} />
            </motion.div>
        ))}
    </div>
);

interface FlatRow {
    node: TreeNode;
    key: string;
    depth: number;
    parentKey: string | null;
    isOpen: boolean;
}

interface TreeRowProps {
    row: FlatRow;
    index: number;
    /** Kleinere Schrift und niedrigere Zeile — siehe `TreeProps.dense`. */
    dense?: boolean;
    reduce: boolean;
    pillId: string;
    query: string;
    isSelected: boolean;
    isHovered: boolean;
    isFocused: boolean;
    /**
     * Welche Zeile überfahren wird — `null`, wenn keine.
     *
     * Das `null` ist nicht bloß Vollständigkeit: Der Kurzhinweis steht als fester Kasten über der
     * Seite und muss geräumt werden, wenn der Zeiger die Zeile verlässt. Ohne diesen Wert bliebe er
     * über allem liegen, was darunter ist.
     */
    onHover: (key: string | null) => void;
    onFocusRow: (key: string) => void;
    onRowKeyDown: (event: React.KeyboardEvent, row: FlatRow) => void;
    onActivate: (row: FlatRow) => void;
    onStartRename: (row: FlatRow) => void;
    onStartCreateFolder: (row: FlatRow) => void;
    onDelete: (row: FlatRow) => void;
    /** Siehe `TreeProps.deleteNonEmptyFolders`. */
    deleteNonEmptyFolders: boolean;
    registerRef: (key: string, el: HTMLDivElement | null) => void;
}

const TreeRow = ({
    row,
    index,
    dense,
    reduce,
    pillId,
    query,
    isSelected,
    isHovered,
    isFocused,
    onHover,
    onFocusRow,
    onRowKeyDown,
    onActivate,
    onStartRename,
    onStartCreateFolder,
    onDelete,
    deleteNonEmptyFolders,
    registerRef,
}: TreeRowProps) => {
    const { t } = useTranslation();
    const { node, depth, isOpen } = row;
    const isFolder = node.type === "folder";
    const isEmptyFolder = isFolder && (node.files?.length ?? 0) === 0;
    const canDelete = !isFolder || isEmptyFolder || deleteNonEmptyFolders;
    /**
     * Aktionen aus dem Menü starten erst, wenn das Menü zu ist.
     *
     * „Umbenennen" und „Neuer Ordner" blenden ein Feld ein, das sich schließt, sobald es den Fokus
     * verliert — und das Menü nimmt ihn ihm zweimal: Solange es offen ist, hält es den Fokus bei
     * sich fest, und beim Schließen gibt es ihn an „Aktionen" zurück. Die Aktion wird deshalb nur
     * vorgemerkt und erst gestartet, wenn das Menü zu ist; den Rückweg des Fokus unterbindet dann
     * `preventDefault`. Ohne das war das Feld weg, bevor jemand tippen konnte.
     *
     * „Löschen" öffnet einen Dialog, und der geht denselben Weg — aus einem anderen Grund: Menü
     * und Dialog sperren beide die Zeigerereignisse der Seite (`pointer-events: none` am `body`)
     * und geben sie beim Schließen wieder frei, jeder auf den Stand, den er beim Öffnen vorfand.
     * Öffnet der Dialog, während das Menü noch zugeht, findet er die Sperre des Menüs vor und
     * stellt genau die beim Schließen wieder her: Die Seite blieb nach „Abbrechen" unbedienbar,
     * bis sie neu geladen wurde.
     */
    const pendingMenuActionRef = useRef<(() => void) | null>(null);

    // Files are drag sources so they can be moved between folders.
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: "FILE",
            item: { id: node.id, name: node.name, path: node.path, type: "file" },
            canDrag: !isFolder,
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [node.id, node.name, node.path, isFolder],
    );

    // Folders accept OS file drops (upload) and internal file drags (move).
    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: [NativeTypes.FILE, "FILE"],
            canDrop: () => isFolder,
            drop: async (item: any, monitor: any) => {
                const folderPath = node.path || "";
                if (monitor.getItemType() === NativeTypes.FILE) {
                    const files = monitor.getItem().files;
                    if (files?.length && node.onFileDrop) {
                        await node.onFileDrop(files, folderPath);
                    }
                } else if (item?.type === "file" && node.onFileMove) {
                    await node.onFileMove(item, folderPath);
                }
            },
            collect: (monitor: any) => ({
                isOver: monitor.isOver({ shallow: true }),
                canDrop: monitor.canDrop(),
            }),
        }),
        [isFolder, node.path, node.onFileDrop, node.onFileMove],
    );

    const isDropTarget = isFolder && isOver && canDrop;

    const setRefs = (el: HTMLDivElement | null) => {
        registerRef(row.key, el);
        if (isFolder) drop(el);
        else drag(el);
    };

    return (
        <motion.div
            layout={reduce ? false : "position"}
            initial={reduce ? false : { opacity: 0, y: -6 }}
            animate={{
                opacity: 1,
                y: 0,
                transition: reduce
                    ? { duration: 0 }
                    : { ...ROW_ENTER, delay: Math.min(index * 0.02, 0.12) },
            }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4, transition: ROW_EXIT }}
            transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
            className="relative"
        >
            <div
                ref={setRefs}
                role="treeitem"
                aria-level={depth + 1}
                aria-selected={isSelected}
                aria-expanded={isFolder ? isOpen : undefined}
                tabIndex={isFocused ? 0 : -1}
                onFocus={() => onFocusRow(row.key)}
                onKeyDown={(event) => onRowKeyDown(event, row)}
                onMouseEnter={() => onHover(row.key)}
                onMouseLeave={() => onHover(null)}
                onClick={() => onActivate(row)}
                className={cn(
                    "group/row relative flex w-full min-w-0 select-none items-center gap-2 overflow-hidden rounded-lg pr-1.5 text-left outline-none transition-colors",
                    dense ? "h-7 text-xs" : "h-9 text-sm",
                    node.dimmed && "opacity-55",
                    "text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                    isFolder ? "cursor-pointer font-medium" : "cursor-grab",
                    isSelected && "bg-muted font-medium text-foreground",
                    isDropTarget && "bg-primary/10 text-foreground ring-1 ring-inset ring-primary/50",
                    isDragging && "cursor-grabbing opacity-40",
                )}
                style={{ paddingLeft: 8 + depth * INDENT }}
            >
                {/* Hover pill gliding between rows (shared layoutId). */}
                <AnimatePresence>
                    {isHovered && !isSelected && !isDropTarget && (
                        <motion.span
                            layoutId={pillId}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
                            className="pointer-events-none absolute inset-0 rounded-lg bg-muted/70"
                        />
                    )}
                </AnimatePresence>

                {/* Branch guide line for nested rows. */}
                {depth > 0 && (
                    <motion.span
                        aria-hidden
                        initial={reduce ? false : { opacity: 0, scaleY: 0 }}
                        animate={{ opacity: 1, scaleY: 1 }}
                        transition={reduce ? { duration: 0 } : BRANCH_DRAW}
                        className="absolute bottom-0 top-0 w-px origin-top bg-border/70"
                        style={{ left: 16 + (depth - 1) * INDENT }}
                    />
                )}

                {/* Chevron — rotates for folders, invisible spacer for files. */}
                <motion.span
                    aria-hidden
                    animate={{ rotate: isFolder && isOpen ? 90 : 0 }}
                    transition={reduce ? { duration: 0 } : SPRING_SWAP}
                    className={cn(
                        "relative z-10 grid size-4 shrink-0 place-items-center",
                        !isFolder && "opacity-0",
                    )}
                >
                    <ChevronRight className="size-3.5" />
                </motion.span>

                {/* Auch Ordner: `showCheckbox` setzt der Aufrufer bereits je
                    Knotenart — der Dateimanager nur an Dateien, der Dateiwähler
                    an Ordner genau dann, wenn er `multiSelect && selectFolders`
                    ist. Ein zusätzliches `!isFolder` hier war für Dateien
                    wirkungslos und hat die Ordnerauswahl des Wählers stumm
                    ausgeschaltet, obwohl der Server sie als `folderPaths`
                    weiterhin entgegennimmt. */}
                {node.showCheckbox && (
                    <span
                        className="relative z-10 flex shrink-0 items-center"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Checkbox
                            aria-label={t("global.select")}
                            checked={node.selected || false}
                            disabled={node.selectionLocked}
                            onCheckedChange={(checked) => node.onSelect?.(checked === true)}
                        />
                    </span>
                )}

                <span
                    aria-hidden
                    className={cn(
                        "relative z-10 grid size-4 shrink-0 place-items-center text-muted-foreground transition-colors group-hover/row:text-foreground",
                        isFolder && isOpen && "text-foreground",
                    )}
                >
                    {isFolder ? (
                        <AnimatedFolderIcon open={isOpen} reduce={reduce} />
                    ) : (
                        <>
                            {fileIconForName(node.name)}
                            {node.iconMark && (
                                /* Rechts unten am Symbol, mit einem Ring in der Zeilenfarbe, damit
                                   der Punkt auch auf der Hover-Fläche abgesetzt bleibt. */
                                <span
                                    aria-hidden
                                    className={cn(
                                        "absolute -bottom-0.5 -right-0.5 size-2 rounded-full ring-2 ring-card",
                                        node.iconMark.tone === "error"
                                            ? "bg-destructive"
                                            : node.iconMark.tone === "warn"
                                                ? "bg-amber-500"
                                                : node.iconMark.tone === "ok"
                                                    ? "bg-emerald-500"
                                                    : "bg-sky-500",
                                    )}
                                />
                            )}
                        </>
                    )}
                </span>

                <span className="relative z-10 min-w-0 flex-1 truncate">
                    <HighlightedName name={node.name} query={query} />
                </span>

                {/**
                  * Abzeichen sind Auskunft, kein Bedienelement.
                  *
                  * Hier lag der Klickfänger der ganzen Zeile: `stopPropagation` saß auf der Spanne,
                  * die *alles* auf der rechten Seite enthält — Abzeichen wie Menü. Nötig ist es nur
                  * für das Menü; die Abzeichen fingen den Klick bloß mit ab. Und weil sie nicht
                  * schrumpfen, der Dateiname daneben aber schon, blieb in einer schmalen Spalte mit
                  * zwei Abzeichen kaum noch eine Stelle übrig, an der ein Klick die Zeile traf.
                  * Von außen sah das aus wie eine Zeile, die auf Klicks nicht reagiert.
                  */}
                <span className="relative z-10 ml-auto flex min-w-0 shrink items-center gap-1.5">
                    {!isFolder && node.badge ? <NodeBadge badge={node.badge} /> : null}
                    {!isFolder && node.secondaryBadge ? <NodeBadge badge={node.secondaryBadge} /> : null}
                    {!isFolder && !node.badge && (
                        <ProcessingBadge
                            processing={node.processing}
                            processingState={node.processingState}
                        />
                    )}
                    {isFolder && node.isUploading && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-primary">
                            <Loader2 aria-hidden className="size-3 animate-spin" />
                            Wird hochgeladen…
                        </span>
                    )}
                    {/* Nur das Menü fängt den Klick ab — es ist das Einzige hier, das selbst
                        etwas damit anfängt. */}
                    <span className="shrink-0" onClick={(event) => event.stopPropagation()}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-7 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100 data-[state=open]:opacity-100"
                                aria-label="Aktionen"
                            >
                                <MoreHorizontal className="size-4" aria-hidden />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-44"
                            onCloseAutoFocus={(event) => {
                                const start = pendingMenuActionRef.current;
                                if (!start) return;
                                pendingMenuActionRef.current = null;
                                event.preventDefault();
                                start();
                            }}
                        >
                            <DropdownMenuItem
                                onSelect={() => {
                                    pendingMenuActionRef.current = () => onStartRename(row);
                                }}
                            >
                                <Pencil className="mr-2 size-4" aria-hidden />
                                Umbenennen
                            </DropdownMenuItem>
                            {isFolder && (
                                <DropdownMenuItem
                                    onSelect={() => {
                                        pendingMenuActionRef.current = () =>
                                            onStartCreateFolder(row);
                                    }}
                                >
                                    <FolderPlus className="mr-2 size-4" aria-hidden />
                                    Neuer Ordner
                                </DropdownMenuItem>
                            )}
                            {!isFolder && node.onView && (
                                <DropdownMenuItem onSelect={() => node.onView?.()}>
                                    <Eye className="mr-2 size-4" aria-hidden />
                                    Ansehen
                                </DropdownMenuItem>
                            )}
                            {!isFolder && (
                                <DropdownMenuItem onSelect={() => node.onDownload?.()}>
                                    <Download className="mr-2 size-4" aria-hidden />
                                    Herunterladen
                                </DropdownMenuItem>
                            )}
                            {canDelete && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onSelect={() => {
                                            pendingMenuActionRef.current = () => onDelete(row);
                                        }}
                                    >
                                        <Trash2 className="mr-2 size-4" aria-hidden />
                                        Löschen
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    </span>
                </span>

                {isFolder && node.isUploading && <UploadShimmer />}
            </div>
        </motion.div>
    );
};

const Tree = ({
    data,
    isLoading = false,
    dense = false,
    title = "Dokumente",
    selectedPath,
    onNodeClick,
    onUpdate,
    onFileDrop,
    onFileMove,
    onCreateRootFolder,
    onRefresh,
    deleteNonEmptyFolders = false,
}: TreeProps) => {
    const reduce = useReducedMotion() ?? false;
    const pillId = useId();

    const [nodes, setNodes] = useState<TreeNode[]>(data || []);
    // User toggles override the isOpen flags delivered with each storage refresh.
    const [expandedOverrides, setExpandedOverrides] = useState<Map<string, boolean>>(
        new Map(),
    );
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    const [focusedKey, setFocusedKey] = useState<string | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    // null = nothing being created; key "" = creating at root.
    const [creatingInKey, setCreatingInKey] = useState<string | null>(null);
    const [query, setQuery] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const rowRefs = useRef(new Map<string, HTMLDivElement>());
    /**
     * Der Kurzhinweis der überfahrenen Zeile — schwebend, außerhalb der Zeile.
     *
     * Er saß einmal als Sprechblase *um* die Beschriftung, und damit lag fremdes Verhalten zwischen
     * dem Zeiger und dem, worauf geklickt wird; die Zeile reagierte danach auf nichts mehr. Was
     * außerhalb hängt und keine Zeigerereignisse annimmt, kann das nicht.
     */
    const [hint, setHint] = useState<{ top: number; left: number; title: string; text?: string | null } | null>(
        null,
    );
    const uploadInputRef = useRef<HTMLInputElement>(null);

    const normalizedQuery = query.trim().toLowerCase();

    useLayoutEffect(() => {
        setNodes(data || []);
    }, [data]);

    useDidMountEffect(() => {
        onUpdate && onUpdate(nodes);
    }, [nodes]);

    const isNodeOpen = useCallback(
        (node: TreeNode) =>
            expandedOverrides.get(node.path ?? "") ?? node.isOpen ?? false,
        [expandedOverrides],
    );

    // While searching, matching subtrees are kept and force-expanded.
    const displayNodes = useMemo(() => {
        if (!normalizedQuery) return nodes;
        const filterForQuery = (items: TreeNode[]): TreeNode[] =>
            items.flatMap((item) => {
                const selfMatch = item.name.toLowerCase().includes(normalizedQuery);
                if (item.type === "file") return selfMatch ? [item] : [];
                if (selfMatch) return [item];
                const files = filterForQuery(item.files || []);
                return files.length ? [{ ...item, files }] : [];
            });
        return filterForQuery(nodes);
    }, [nodes, normalizedQuery]);

    const rows = useMemo(() => {
        const result: FlatRow[] = [];
        const walk = (items: TreeNode[], depth: number, parentKey: string | null) => {
            items.forEach((node) => {
                const key = nodeKey(node);
                const isOpen =
                    node.type === "folder" && (normalizedQuery ? true : isNodeOpen(node));
                result.push({ node, key, depth, parentKey, isOpen });
                if (isOpen && node.files?.length) {
                    walk(node.files, depth + 1, key);
                }
            });
        };
        walk(displayNodes, 0, null);
        return result;
    }, [displayNodes, isNodeOpen, normalizedQuery]);

    /**
     * Nach kurzem Verweilen, nicht sofort.
     *
     * Wer mit dem Zeiger über eine Liste fährt, überfährt zwanzig Zeilen; ein Hinweis, der bei jeder
     * aufblitzt, ist ein Ärgernis. Umgekehrt darf er nicht so lange auf sich warten lassen, dass man
     * ihn für kaputt hält — dazwischen liegt eine Viertelsekunde.
     */
    useEffect(() => {
        const row = rows.find((entry) => entry.key === hoveredKey);

        if (!hoveredKey || !row?.node.hint) {
            setHint(null);
            return;
        }

        const { title, text } = row.node.hint;

        const timer = setTimeout(() => {
            const element = rowRefs.current.get(hoveredKey);
            if (!element) return;

            const rect = element.getBoundingClientRect();

            setHint({
                ...hintPlacement(
                    rect,
                    { width: HINT_WIDTH, height: text ? 96 : 40 },
                    { width: window.innerWidth, height: window.innerHeight },
                ),
                title,
                text,
            });
        }, HINT_DELAY_MS);

        return () => clearTimeout(timer);
    }, [hoveredKey, rows]);

    // Clear hover state when scrolling to prevent the fixed hint from staying visible
    useEffect(() => {
        const handleScroll = () => setHoveredKey(null);
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, []);

    const counts = useMemo(() => {
        let folders = 0;
        let files = 0;
        const walk = (items: TreeNode[]) => {
            items.forEach((item) => {
                if (item.type === "folder") {
                    folders += 1;
                    if (item.files) walk(item.files);
                } else {
                    files += 1;
                }
            });
        };
        walk(nodes);
        return { folders, files };
    }, [nodes]);

    // --- local tree mutations (optimistic UI; the server refresh reconciles later) ---

    const mapTree = (items: TreeNode[], fn: (n: TreeNode) => TreeNode): TreeNode[] =>
        items.map((item) => {
            const next = fn(item);
            if (next.files) return { ...next, files: mapTree(next.files, fn) };
            return next;
        });

    const filterTree = (items: TreeNode[], keep: (n: TreeNode) => boolean): TreeNode[] =>
        items
            .filter(keep)
            .map((item) =>
                item.files ? { ...item, files: filterTree(item.files, keep) } : item,
            );

    const renameLocalNode = (key: string, name: string) => {
        setNodes((prev) => mapTree(prev, (n) => (nodeKey(n) === key ? { ...n, name } : n)));
    };

    const removeLocalNode = (key: string) => {
        setNodes((prev) => filterTree(prev, (n) => nodeKey(n) !== key));
    };

    const insertLocalFolder = (parentKey: string | null, name: string, path: string) => {
        const bare: TreeNode = { type: "folder", name, path, files: [], isOpen: false };
        if (parentKey === null) {
            setNodes((prev) => [...prev, bare]);
            return;
        }
        setNodes((prev) =>
            mapTree(prev, (n) =>
                nodeKey(n) === parentKey ? { ...n, files: [...(n.files || []), bare] } : n,
            ),
        );
    };

    // --- interactions ---

    const toggleFolder = (row: FlatRow) => {
        const path = row.node.path ?? "";
        const next = !row.isOpen;
        setExpandedOverrides((prev) => {
            const map = new Map(prev);
            map.set(path, next);
            return map;
        });
        row.node.onToggleOpen?.(next);
    };

    const activateRow = (row: FlatRow) => {
        setFocusedKey(row.key);
        if (row.node.type === "folder") {
            toggleFolder(row);
            onNodeClick?.({
                state: nodes,
                name: row.node.name,
                level: row.depth + 1,
                path: row.node.path,
                type: "folder",
            });
        } else {
            row.node.onClick?.();
            onNodeClick?.({
                state: nodes,
                name: row.node.name,
                parent: row.node.parent,
                type: "file",
                // Der Pfad fehlte hier, anders als beim Ordner. Ohne ihn kann ein Aufrufer die
                // angeklickte Datei nicht benennen und muss den Rückruf am Knoten festmachen —
                // also an einem Abschluss, der so alt ist wie die letzte Auflistung.
                path: row.node.path,
            });
        }
    };

    const startRename = (row: FlatRow) => {
        setCreatingInKey(null);
        setEditingKey(row.key);
    };

    const startCreateFolder = (row: FlatRow) => {
        setEditingKey(null);
        // Make sure the target folder is open so the input row is visible.
        if (!row.isOpen) toggleFolder(row);
        setCreatingInKey(row.key);
    };

    const deleteRow = (row: FlatRow) => {
        row.node.onDelete?.(() => removeLocalNode(row.key));
    };

    const submitRename = async (row: FlatRow, name: string) => {
        await row.node.onRename?.(name);
        renameLocalNode(row.key, name);
        setEditingKey(null);
    };

    const submitCreateFolder = async (parentRow: FlatRow | null, name: string) => {
        if (parentRow === null) {
            await onCreateRootFolder?.(name);
            insertLocalFolder(null, name, `/${name}/`);
        } else {
            const parentPath = parentRow.node.path ?? "/";
            const newPath = `${parentPath}${name}/`.replace("//", "/");
            const result = parentRow.node.onCreateFolder?.(name, () => {
                insertLocalFolder(parentRow.key, name, newPath);
            });
            await Promise.resolve(result as unknown);
        }
        setCreatingInKey(null);
    };

    const focusRow = useCallback((key: string) => {
        setFocusedKey(key);
        const el = rowRefs.current.get(key);
        if (el) el.focus();
        else requestAnimationFrame(() => rowRefs.current.get(key)?.focus());
    }, []);

    const handleRowKeyDown = (event: React.KeyboardEvent, row: FlatRow) => {
        const index = rows.findIndex((r) => r.key === row.key);
        const previous = rows[index - 1];
        const next = rows[index + 1];
        const isFolder = row.node.type === "folder";

        if (event.key === "ArrowDown" && next) {
            event.preventDefault();
            focusRow(next.key);
        } else if (event.key === "ArrowUp" && previous) {
            event.preventDefault();
            focusRow(previous.key);
        } else if (event.key === "Home" && rows[0]) {
            event.preventDefault();
            focusRow(rows[0].key);
        } else if (event.key === "End" && rows.length > 0) {
            event.preventDefault();
            focusRow(rows[rows.length - 1].key);
        } else if (event.key === "ArrowRight" && isFolder) {
            event.preventDefault();
            if (!row.isOpen) toggleFolder(row);
            else if (next?.parentKey === row.key) focusRow(next.key);
        } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            if (isFolder && row.isOpen) toggleFolder(row);
            else if (row.parentKey) focusRow(row.parentKey);
        } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateRow(row);
        }
    };

    const registerRef = useCallback((key: string, el: HTMLDivElement | null) => {
        if (el) rowRefs.current.set(key, el);
        else rowRefs.current.delete(key);
    }, []);

    // Root drop zone: uploads and moves that land outside any folder row.
    const [{ isOver: isRootOver, canDrop: canRootDrop }, rootDrop] = useDrop(
        () => ({
            accept: [NativeTypes.FILE, "FILE"],
            drop: async (item: any, monitor: any) => {
                if (monitor.didDrop()) return;
                if (monitor.getItemType() === NativeTypes.FILE) {
                    const files = monitor.getItem().files;
                    if (files?.length && onFileDrop) {
                        await onFileDrop(files, "");
                    }
                } else if (item?.type === "file" && onFileMove) {
                    await onFileMove(item, "");
                }
            },
            collect: (monitor: any) => ({
                isOver: monitor.isOver({ shallow: true }),
                canDrop: monitor.canDrop(),
            }),
        }),
        [onFileDrop, onFileMove],
    );

    const firstRowKey = rows[0]?.key ?? null;
    const effectiveFocusedKey =
        focusedKey && rows.some((r) => r.key === focusedKey) ? focusedKey : firstRowKey;

    const isEmpty = !isLoading && rows.length === 0;

    return (
        <div className="flex w-full flex-col">
            {/* Header: title, counts, search, upload, create folder */}
            {/* -mx-4 gleicht das p-4 des umgebenden Panes aus, damit die
                Trennlinie bis an beide Ränder läuft; px-5 stellt die alte
                Innenflucht (p-4 + px-1) wieder her. */}
            <div
                className={cn(
                    "mb-2 flex flex-wrap items-center gap-2 border-b pb-3",
                    // Der negative Rand gleicht das `p-4` der Dateiverwaltung aus, damit die
                    // Trennlinie bis an beide Ränder läuft. Gedrängt sitzt der Baum ohne dieses
                    // Polster — dann zöge der Ausgleich ihn aus seinem Behälter heraus.
                    dense ? "px-1" : "-mx-4 px-5",
                )}
            >
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
                    <FolderTree className="size-4" aria-hidden />
                </span>
                <div className="mr-auto">
                    <div className="text-sm font-semibold leading-tight">{title}</div>
                    <div className="text-xs text-muted-foreground">
                        {isLoading
                            ? "Wird geladen…"
                            : normalizedQuery
                                ? `${rows.length} Treffer`
                                : `${counts.folders} Ordner · ${counts.files} Datei${counts.files === 1 ? "" : "en"}`}
                    </div>
                </div>
                <TooltipProvider delayDuration={200}>
                    <div className="flex items-center gap-1">
                        {/* Suche: zugeklappt nur die Lupe; offen wird sie zum Feld
                            mit Lupe als Prefix und X zum Schließen — ein Control,
                            kein Icon neben dem Feld. */}
                        <AnimatePresence initial={false}>
                            {isSearchOpen && (
                                <motion.div
                                    key="search-field"
                                    initial={{ width: 32, opacity: 0 }}
                                    animate={{ width: 224, opacity: 1 }}
                                    exit={{ width: 32, opacity: 0 }}
                                    transition={{ duration: 0.2, ease: EASE_OUT }}
                                    className="relative overflow-hidden"
                                >
                                    <Search
                                        aria-hidden
                                        className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                                    />
                                    <Input
                                        autoFocus
                                        value={query}
                                        onChange={(event) => setQuery(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Escape") {
                                                if (query) {
                                                    setQuery("");
                                                } else {
                                                    setIsSearchOpen(false);
                                                }
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!query.trim()) setIsSearchOpen(false);
                                        }}
                                        placeholder="Suchen…"
                                        aria-label="Dateien durchsuchen"
                                        className="h-8 w-full rounded-lg border-transparent bg-muted pl-8 pr-8 text-sm shadow-none focus-visible:border-transparent focus-visible:ring-0"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:text-foreground"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => {
                                            setQuery("");
                                            setIsSearchOpen(false);
                                        }}
                                        aria-label="Suche schließen"
                                    >
                                        <X className="size-3.5" aria-hidden />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {!isSearchOpen && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 text-muted-foreground hover:text-foreground"
                                        onClick={() => setIsSearchOpen(true)}
                                        aria-label="Suchen"
                                    >
                                        <Search className="size-4" aria-hidden />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="text-sm font-medium">Suchen</TooltipContent>
                            </Tooltip>
                        )}
                        {onFileDrop && (
                            <>
                                <input
                                    ref={uploadInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={async (event) => {
                                        const files = Array.from(event.target.files || []);
                                        event.target.value = "";
                                        if (files.length) await onFileDrop(files, "");
                                    }}
                                />
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="size-8 text-muted-foreground hover:text-foreground"
                                            onClick={() => uploadInputRef.current?.click()}
                                            aria-label="Dateien hochladen"
                                        >
                                            <UploadCloud className="size-4" aria-hidden />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="text-sm font-medium">Dateien hochladen</TooltipContent>
                                </Tooltip>
                            </>
                        )}
                        {onCreateRootFolder && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 text-muted-foreground hover:text-foreground"
                                        onClick={() => {
                                            setEditingKey(null);
                                            setCreatingInKey("");
                                        }}
                                        disabled={creatingInKey === ""}
                                        aria-label="Neuer Ordner"
                                    >
                                        <FolderPlus className="size-4" aria-hidden />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="text-sm font-medium">Neuer Ordner</TooltipContent>
                            </Tooltip>
                        )}
                        {onRefresh && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 text-muted-foreground hover:text-foreground"
                                        disabled={isRefreshing}
                                        onClick={async () => {
                                            setIsRefreshing(true);
                                            try {
                                                await onRefresh();
                                            } finally {
                                                setIsRefreshing(false);
                                            }
                                        }}
                                        aria-label="Dateiliste aktualisieren"
                                    >
                                        <RefreshCw
                                            className={cn("size-4", isRefreshing && "animate-spin")}
                                            aria-hidden
                                        />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="text-sm font-medium">Dateiliste aktualisieren</TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </TooltipProvider>
            </div>

            <div
                ref={rootDrop}
                className={cn(
                    "relative min-h-[120px] rounded-xl border-2 border-dashed border-transparent p-1 transition-colors duration-200",
                    isRootOver && canRootDrop && "border-primary/50 bg-primary/[0.04]",
                )}
            >
                {/* Root folder creation input */}
                <AnimatePresence initial={false}>
                    {creatingInKey === "" && (
                        <motion.div
                            key="create-root"
                            initial={reduce ? false : { opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0, transition: ROW_ENTER }}
                            exit={{ opacity: 0, transition: ROW_EXIT }}
                        >
                            <InlineNameRow
                                depth={0}
                                type="folder"
                                onSubmit={(name) => submitCreateFolder(null, name)}
                                onCancel={() => setCreatingInKey(null)}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {isLoading ? (
                    <TreeSkeleton />
                ) : isEmpty && normalizedQuery ? (
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, ease: EASE_OUT }}
                        className="flex flex-col items-center justify-center gap-2 rounded-lg py-10 text-center"
                    >
                        <span className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground">
                            <SearchX className="size-5" aria-hidden />
                        </span>
                        <div className="text-sm font-medium text-foreground">Keine Treffer</div>
                        <div className="max-w-[280px] text-xs text-muted-foreground">
                            Keine Dateien oder Ordner passen zu „{query.trim()}“.
                        </div>
                    </motion.div>
                ) : isEmpty ? (
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: EASE_OUT }}
                        className="flex flex-col items-center justify-center gap-2 rounded-lg py-10 text-center"
                    >
                        <motion.span
                            animate={
                                isRootOver && canRootDrop
                                    ? { y: -4, scale: 1.08 }
                                    : { y: 0, scale: 1 }
                            }
                            transition={reduce ? { duration: 0 } : SPRING_SWAP}
                            className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground"
                        >
                            <Upload className="size-5" aria-hidden />
                        </motion.span>
                        <div className="text-sm font-medium text-foreground">
                            Noch keine Dateien
                        </div>
                        <div className="max-w-[280px] text-xs text-muted-foreground">
                            Dateien hierher ziehen oder über einen Ordner ablegen, um sie
                            hochzuladen.
                        </div>
                    </motion.div>
                ) : (
                    <div role="tree" aria-label="Dateien" className="flex w-full flex-col">
                        <AnimatePresence initial={false}>
                            {rows.map((row, index) => (
                                <React.Fragment key={row.key}>
                                    {editingKey === row.key ? (
                                        <motion.div
                                            key={`${row.key}-edit`}
                                            initial={reduce ? false : { opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0, transition: ROW_EXIT }}
                                        >
                                            <InlineNameRow
                                                depth={row.depth}
                                                type={row.node.type}
                                                defaultValue={row.node.name}
                                                onSubmit={(name) => submitRename(row, name)}
                                                onCancel={() => setEditingKey(null)}
                                            />
                                        </motion.div>
                                    ) : (
                                        <TreeRow
                                            row={row}
                                            index={index}
                                            dense={dense}
                                            reduce={reduce}
                                            pillId={pillId}
                                            query={normalizedQuery}
                                            isSelected={
                                                !!selectedPath &&
                                                row.node.type === "file" &&
                                                row.node.path === selectedPath
                                            }
                                            isHovered={hoveredKey === row.key}
                                            isFocused={effectiveFocusedKey === row.key}
                                            onHover={setHoveredKey}
                                            onFocusRow={setFocusedKey}
                                            onRowKeyDown={handleRowKeyDown}
                                            onActivate={activateRow}
                                            onStartRename={startRename}
                                            onStartCreateFolder={startCreateFolder}
                                            onDelete={deleteRow}
                                            deleteNonEmptyFolders={deleteNonEmptyFolders}
                                            registerRef={registerRef}
                                        />
                                    )}
                                    {creatingInKey === row.key && (
                                        <motion.div
                                            key={`${row.key}-create`}
                                            initial={reduce ? false : { opacity: 0, y: -6 }}
                                            animate={{ opacity: 1, y: 0, transition: ROW_ENTER }}
                                            exit={{ opacity: 0, transition: ROW_EXIT }}
                                        >
                                            <InlineNameRow
                                                depth={row.depth + 1}
                                                type="folder"
                                                onSubmit={(name) => submitCreateFolder(row, name)}
                                                onCancel={() => setCreatingInKey(null)}
                                            />
                                        </motion.div>
                                    )}
                                </React.Fragment>
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Root drop hint while dragging over the free area */}
                <AnimatePresence>
                    {isRootOver && canRootDrop && !isEmpty && (
                        <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15, ease: EASE_OUT }}
                            className="pointer-events-none mt-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs text-primary"
                        >
                            <Upload className="size-3.5" aria-hidden />
                            Im Stammverzeichnis ablegen
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Der Kurzhinweis, schwebend und taub.
                `pointer-events-none` ist hier nicht Kosmetik, sondern die ganze Bedingung: Er steht
                über der Seite, und alles, was dort Zeigerereignisse annimmt, nimmt sie jemandem
                anders weg. */}
            {hint ? (
                <div
                    role="tooltip"
                    /* `pointerEvents` steht hier im Stil und nicht nur als Klasse: An dieser einen
                       Eigenschaft hängt, ob die Zeilen darunter noch bedienbar sind, und dafür will
                       ich mich nicht darauf verlassen, dass ein Stylesheet geladen ist. */
                    style={{ top: hint.top, left: hint.left, width: HINT_WIDTH, pointerEvents: 'none' }}
                    className="pointer-events-none fixed z-50 rounded-md bg-primary px-3 py-2 text-primary-foreground shadow-md"
                >
                    <div className="break-words text-xs font-semibold">{hint.title}</div>
                    {hint.text ? (
                        <p className="mt-1 break-words text-[11px] leading-snug opacity-90">{hint.text}</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default Tree;
